/**
 * Admin state management with Zustand
 */
import { create } from 'zustand';
import api from '../services/api';
import { getCommonErrorMessage } from '../utils/errorHandler';
import i18n from '../i18n';

const useAdminStore = create((set, get) => ({
  // Dashboard data
  dashboard: null,
  loading: false,
  error: null,

  // Cache clearing
  clearingCache: false,

  // ============================================
  // Dashboard Actions
  // ============================================

  loadDashboard: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.get('/admin/dashboard');
      set({
        dashboard: response.data,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      const errorMessage = getCommonErrorMessage(error, i18n.t.bind(i18n));
      
      set({
        loading: false,
        error: errorMessage,
      });
    }
  },

  // ============================================
  // Cache Actions
  // ============================================

  clearCache: async () => {
    set({ clearingCache: true });
    try {
      const response = await api.post('/admin/cache/clear');
      console.log('Cache cleared:', response.data);
      set({ clearingCache: false });
      return response.data;
    } catch (error) {
      console.error('Error clearing cache:', error);
      set({ clearingCache: false });
      throw error;
    }
  },

  getCacheStats: async () => {
    try {
      const response = await api.get('/admin/cache/stats');
      return response.data;
    } catch (error) {
      console.error('Error getting cache stats:', error);
      throw error;
    }
  },

  // ============================================
  // Service Health & Metrics
  // ============================================

  getServicesHealth: async () => {
    try {
      const response = await api.get('/admin/services/health');
      return response.data;
    } catch (error) {
      console.error('Error getting services health:', error);
      throw error;
    }
  },

  getRealtimeMetrics: async () => {
    try {
      const response = await api.get('/admin/metrics/realtime');
      return response.data;
    } catch (error) {
      console.error('Error getting realtime metrics:', error);
      throw error;
    }
  },

  // ============================================
  // User Management
  // ============================================

  listUsers: async () => {
    try {
      const response = await api.get('/admin/users');
      return response.data;
    } catch (error) {
      console.error('Error listing users:', error);
      throw error;
    }
  },

  createUsers: async (userData) => {
    try {
      const response = await api.post('/admin/users', userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  deactivateUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  },

  listDepartments: async () => {
    try {
      const response = await api.get('/admin/departments');
      return response.data;
    } catch (error) {
      console.error('Error listing departments:', error);
      throw error;
    }
  },

  // ============================================
  // Session Management
  // ============================================

  getActiveSessions: async () => {
    try {
      const response = await api.get('/admin/sessions/active');
      return response.data;
    } catch (error) {
      console.error('Error getting active sessions:', error);
      throw error;
    }
  },

  // ============================================
  // Cleanup
  // ============================================

  cleanup: () => {
    set({
      dashboard: null,
      loading: false,
      error: null,
      clearingCache: false,
    });
  },
}));

export default useAdminStore;

